import _ from 'underscore';
import $ from 'jquery';
import 'bootstrap';
import Backbone from 'backbone';
import template from './template/index.pug';

/**
 * a button of dialog
 * @typedef {object} button
 * @param {string} text - text of button
 * @param {string} name - name of button, will trigger event 'click button.name' when click button
 * @param {string} class - customize class of button
 */

export class Dialog extends Backbone.View {
  /**
   * override initialize of backbone view
   * @param {object} options - options
   * @param {string} options.id - set custom dialog id. If not set, a unique value will be set.
   * @param {string} options.backdrop - controls presence of a backdrop. Allowed values: true
   * , false (no backdrop), 'static'(default) - backdrop is present but modal window is not
   * closed when clicking outside of the modal window.
   * @param {string} options.keyboard - Allowed values: false(default), true - closes the modal
   * when escape key is pressed
   * @param {boolean} options.showHeader - flag for whether or not to show .modal-header
   * @param {boolean} options.showBody - flag for whether or not to show .modal-body for scenarios
   * where contentView will never be set.
   * @returns {undefined} - no return
   */
  initialize({
    // eslint-disable-next-line no-unused-vars
    id, // it will be auto-set by backbone
    backdrop,
    keyboard,
    showHeader = true,
    showBody = true,
    showCloseButton = true,
  } = {}) {
    this.contentView = null;
    this.backdrop = _.isBoolean(backdrop) ? backdrop : 'static';
    this.keyboard = _.isBoolean(keyboard) ? keyboard : false;
    this.showBody = showBody;
    this.showHeader = showHeader;
    this.showCloseButton = showCloseButton;
  }

  /**
   * define events and correspond handler in Dialog
   */
  get events() {
    return {
      'click .modal-footer button': 'buttonClicked',
      'hidden.bs.modal': 'remove',
    };
  }

  /**
   * dialog Id
   * @param {string} dialogId - set dialog's id
   */
  set id(dialogId) {
    if (dialogId) {
      this.dialogId = dialogId;
    }
  }

  get id() {
    if (!this.dialogId) {
      this.dialogId = _.uniqueId('dialog-');
    }
    return this.dialogId;
  }

  /**
   * handle click button event
   * @param {Event} e - jquery event
   * @returns {Undefined} -
   */
  buttonClicked(e) {
    const name = $(e.target).attr('button-name');
    this.trigger('clickedButton', name);
  }

  /**
   * set content of dialog
   * @param {Backbone.View} view - view instance
   * @returns {Dialog} - return Dialog view itself
   */
  setContent(view) {
    if (this.contentView) {
      this.contentView.remove();
    }
    this.contentView = view;

    if (this.el.parentNode) {
      this.contentView.render();
      this.$('.modal-body').append(this.contentView.el);
    }

    return this;
  }

  /**
   * set state of view
   * @param {object} state - state of dialog
   * @param {string} state.title - title of dialog
   * @param {button[]} state.buttons - button list of dialog
   * @returns {Dialog} - return Dialog view itself
   */
  setState(state) {
    this.title = state.title;
    this.buttons = state.buttons;
    return this;
  }

  render() {
    this.$el.html(template({
      title: this.title,
      id: this.id,
      buttons: this.buttons || [],
      showHeader: this.showHeader && (this.title || this.showCloseButton),
      showCloseButton: this.showCloseButton,
      showBody: this.showBody,
    }));

    if (this.contentView) {
      this.contentView.render();
      this.$('.modal-body').append(this.contentView.el);
    }

    return this;
  }

  /**
   * show dialog
   * @returns {Dialog} - return dialog itself
   */
  show() {
    this.render();

    this.$('.modal').modal({
      backdrop: this.backdrop,
      keyboard: this.keyboard,
    });
    return this;
  }

  /**
   * remove view in dialog and itself
   * @returns {Dialog} return itself
   */
  remove() {
    this.trigger('willClose');
    if (this.contentView) {
      this.contentView.remove();
      this.contentView = null;
    }
    this.$('.modal').modal('hide');
    return super.remove();
  }
}
